	// HumanizerInput.tsx (Applying JSX changes for latest SCSS)

import React, { useState, RefObject, useRef, useEffect, useCallback, useMemo } from "react";
import ReactGA from "react-ga4";
import styles from "./style.module.scss"; // Uses the updated SCSS file
import { ButtonPrimary, CopyElement, TextDetectionResult } from "@components";
import { NonLoggedInAnimation } from "./NonLoggedInAnimation";

// --- IMPORT YOUR ICONS (Ensure paths are correct) ---
import { ReactComponent as AiIcon } from "../../assets/icons/AI.svg";
import { ReactComponent as StickIcon } from "../../assets/icons/Stick.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/InfoCircle.svg"; // <-- Check INFO ICON import
import { ReactComponent as CopyIcon } from "./assets/svg/Copy.svg";
import { ReactComponent as SampleIcon } from "./assets/svg/Sample.svg";
// ---------------------------------------------------

import { SAMPLE_TEXT } from "./constants"; // Check path
import { Action } from "./components"; // Check path
import { useUnit } from "effector-react";
import { combine } from "effector";
import {
	$user,
	$userLanguage,
	createAnonymousPaymentFx,
	userFx,
} from "../../features/app/model";
import { useLocation, useNavigate } from "react-router-dom";
import {
	$aiDetect,
	$aiReDetect,
	$humanizedText,
	$humanizerInputValue,
	aiDetectFx,
	aiReDecectFx, // Using name from snippet 1
	humanizeFx,
	rehumanizeFx,
	humanizerInputHandler,
} from "../../features/humanizer/model"; // Check path
import { $pricing, pricingFx } from "../../features/pricing/model";
import { useTranslation } from "react-i18next";
import {
	makeToast,
	makeSubscriptionToast,
	makeEnhancedModelToast,
	setUpgradePopupCallback,
} from "../../shared/ui/toast"; // Check path
import { Switch } from "../../shared/ui/switch"; // Check path
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../shared/ui/tooltip"; // Check path
import { subscribeFx } from "../../features/pricing/model";
import { UpgradePopup } from '../UpgradePopup';
import { upgradeModalOpened } from '../../features/upgrade-modal/model';

// --- Logic from Snippet 1 ---
const countWords = (str: string): number => {
	// Ensure return type is number
	str = str.trim();
	if (!str) return 0; // Handle empty string
	return str.split(/\s+/).length;
};

type AnimationStatus = "hidden" | "animating" | "finished";

export const HumanizerInput: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
	// Use a single state machine for the animation to prevent race conditions
	const [animationStatus, setAnimationStatus] = useState<AnimationStatus>("hidden");

	// --- Constants & Refs (From Snippet 1 / Combined) ---
	const minHeight = 300;
	const maxHeight = 700;
	const defHeight = 350;

	const inputTextareaRef = useRef<HTMLTextAreaElement>(null);
	const outputTextareaRef = useRef<HTMLTextAreaElement>(null);
	const resizeHandleRef = useRef<HTMLDivElement>(null);
	const ref = useRef<HTMLDivElement>(null); // Ref for the main container

	// --- State Variables (From Snippet 1 / Combined) ---
	const [textareaHeight, setTextareaHeight] = useState(minHeight);
	const [isDragging, setIsDragging] = useState(false);
	const [manuallyResized, setManuallyResized] = useState(false);
	const [isHumanizedResultShown, setIsHumanizedResultShown] = useState(false); // Keep state from snippet 1
	const [textDetectionShow, setTextDetectionShow] = useState(false);
	const [aiReDetectShow, setAiReDetectShow] = useState(false);
	const [maxWords, setMaxWords] = useState(300);
	const [isProModel, setIsProModel] = useState(false);
	// State for smooth dragging
	const dragInfoRef = useRef<{ startY: number; startHeight: number } | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [purchaseHandled, setPurchaseHandled] = useState(false);

	// --- Effector Hooks (From Snippet 1 / Combined) ---
	const { t } = useTranslation();
	// Use combined loading state for humanizer/rehumanizer as per snippet 1
	const humanizerLoading = useUnit(
		combine(humanizeFx.pending, rehumanizeFx.pending, (a, b) => a || b),
	);
	const humanizedText = useUnit($humanizedText);
	const aiDetectLoading = useUnit(aiDetectFx.pending); // Input detect loading
	const reDetectAi = useUnit($aiReDetect); // Output detect result data
	const reDetectLoading = useUnit(aiReDecectFx.pending); // Output detect loading
	const inputValue = useUnit($humanizerInputValue);
	const aiDetectRes = useUnit($aiDetect); // Input detect result data
	const user = useUnit($user);
	const navigate = useNavigate();
    const location = useLocation();
	const pricing = useUnit($pricing);
	const getPricing = useUnit(pricingFx);
	const subscribeQuery = useUnit(subscribeFx);
	const createAnonymousPayment = useUnit(createAnonymousPaymentFx);

	// --- Derived State ---
	const isTrial = useMemo(() => {
		if (!user || !user.subscription) {
			return true; // Or handle as not subscribed
		}
		return user.subscription.title?.toUpperCase() === 'TRIAL'
	}, [user])

	// Log trial status for debugging
	useEffect(() => {
		console.log("[HumanizerInput] User object:", user);
		console.log("[HumanizerInput] User subscription:", user?.subscription);
		console.log("[HumanizerInput] Subscription title:", user?.subscription?.title);
		console.log("[HumanizerInput] Is Trial (computed):", isTrial);
		console.log("[HumanizerInput] Has user:", !!user);
		console.log("[HumanizerInput] Has subscription:", !!user?.subscription);
	}, [user, isTrial]);

	useEffect(() => {
		getPricing({ languageCode: "EN" });
	}, [getPricing]);

	const handleUpgrade = async () => {
		// This is an adaptation of the working `handleGetStarted` from the provided document.
		const weeklyPlan = (pricing as any[])?.find(
			(p) => p.period === "day" && p.isPricingPage,
		);

		if (!weeklyPlan) {
			navigate("/pricing");
			return;
		}

		console.log("Making payment request. User object:", user);
		
		const planData = {
			subscriptionId: weeklyPlan.subscriptionId,
			title: weeklyPlan.title,
			amount: weeklyPlan.amount,
		};

		// Analytics and tracking events
		ReactGA.event("initiate_checkout", {
			currency: "USD",
			value: planData.amount,
			items: [{
				item_id: planData.subscriptionId,
				item_name: planData.title,
				price: planData.amount,
			}],
		});

		if (window.dataLayer) {
			window.dataLayer.push({
				event: 'ads_initiate_checkout',
				value: planData.amount,
				currency: "USD",
				items: [{
					item_id: planData.subscriptionId,
					item_name: planData.title,
					price: planData.amount,
				}],
				user_email: user?.email,
			});
		}

		try {
			let orderResponse: any;

			// --- This is the key logic ---
			if (user && user.role === "ANONYMOUS") {
				// For anonymous users
				console.log("Calling createAnonymousPayment with:", weeklyPlan.subscriptionId);
				orderResponse = await createAnonymousPayment(weeklyPlan.subscriptionId);
			} else {
				// For registered users
				console.log("Calling subscribeQuery with:", weeklyPlan.subscriptionId);
				orderResponse = await subscribeQuery({
					subscriptionId: weeklyPlan.subscriptionId,
					frontendUrl: window.location.origin,
				});
			}
			// -----------------------------

			if (!orderResponse) {
				throw new Error("Failed to create payment session");
			}

			if (orderResponse.sessionUrl) {
				if (orderResponse.value !== undefined) {
					localStorage.setItem('pendingPurchaseValue', orderResponse.value.toString());
				}
				localStorage.setItem('pendingPurchasePlan', JSON.stringify({ ...planData, sessionId: orderResponse.sessionId, paymentId: orderResponse.paymentId }));
				window.location.href = orderResponse.sessionUrl;
			}
		} catch (error) {
			console.error("Payment error:", error);
			// Optionally navigate to an error page or show a toast
			navigate("/pricing");
		}
	};

	// --- Effects ---
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.has('new_subscription') && user && !purchaseHandled) {
			setPurchaseHandled(true);
			(window as any).triggerConfetti();
	
			const pendingPurchasePlanString = localStorage.getItem('pendingPurchasePlan');
			if (pendingPurchasePlanString) {
				try {
					const pendingPurchasePlan = JSON.parse(pendingPurchasePlanString);
					const purchaseParams = {
						transaction_id: pendingPurchasePlan.sessionId,
						value: pendingPurchasePlan.amount,
						currency: pendingPurchasePlan.currency.code,
						items: [
							{
								item_id: pendingPurchasePlan.subscriptionId,
								item_name: pendingPurchasePlan.title,
								price: pendingPurchasePlan.amount,
							},
						],
					};

					// Fire GA4 analytics event
					ReactGA.event('purchase', purchaseParams);
					
					// ⭐ НОВОЕ: Only fire ads_purchase if it wasn't already tracked by App.tsx
					if (!sessionStorage.getItem('ads_purchase_tracked_new_sub')) {
						if (window.dataLayer) {
							window.dataLayer.push({
								event: 'ads_purchase',
								transaction_id: purchaseParams.transaction_id,
								value: purchaseParams.value,
								currency: purchaseParams.currency,
								items: purchaseParams.items,
								user_email: user.email,
							});
							console.log('🚀 Fired ads_purchase event for Stripe/Paddle in HumanizerInput', purchaseParams);
						}
						// Mark as tracked to prevent duplicate events
						sessionStorage.setItem('ads_purchase_tracked_new_sub', 'true');
					} else {
						console.log('⏭️ Skipping duplicate ads_purchase event (already tracked by App.tsx)');
					}
					// ⭐ конец нового блока
					
					localStorage.removeItem('pendingPurchasePlan');

				} catch (error) {
					console.error("Error processing pending purchase plan:", error);
				}
			}
	
			localStorage.setItem(
				'subscriptionStartTime',
				new Date().getTime().toString(),
			)
			userFx()
			
			console.log('💰 Purchase completed, letting App.tsx handle upsell popup...');
		}
    }, [location, user, purchaseHandled]);

	// Set Max Words Effect (From Snippet 1)
	useEffect(() => {
		if (user && user.subscription) {
			setMaxWords(user.subscription.maxWords || 300);

			// Check if there's pending text to process after login
			const pendingText = localStorage.getItem("pendingHumanizeText");
			if (pendingText && pendingText.trim().length > 2) {
				console.log(
					"Found pending text to process after login:",
					pendingText.substring(0, 20) + "...",
				);

				// Set the input value to the saved text using the effector handler
				humanizerInputHandler(pendingText);

				// Don't remove the text from localStorage yet - wait until processing is complete

				// Create a custom function to process the text with validation
				const processStoredText = async () => {
					const isTrialUser = user?.subscription?.title?.toUpperCase() === "TRIAL";
					console.log("Processing stored text", {
						textLength: pendingText.trim().length,
						wordCount: countWords(pendingText),
						maxWords,
						requestsLeft: user?.subscription?.requestsLeft,
						isTrialUser,
					});

					// Validate the text length again before processing
					if (pendingText.trim().length > 2 && countWords(pendingText) <= maxWords) {
						// For trial users, run AI detection on input text first
						if (isTrialUser) {
							console.log("TRIAL USER: Running input AI detection before auto-humanize");
							// Decrement requests for input AI detection
							if (user?.subscription && user.subscription.requestsLeft > 0) {
								user.subscription.requestsLeft--;
								console.log(
									"Decremented request for input AI detection, remaining:",
									user.subscription.requestsLeft,
								);
							}
							setTextDetectionShow(true);
							await aiDetectFx({ text: pendingText });
						}

						// Process the request if user has enough requests
						const enoughRequests = user?.subscription?.requestsLeft > 0;
						if (enoughRequests) {
							// Decrement requests left
							if (user?.subscription) {
								user.subscription.requestsLeft--;
							}

							setIsHumanizedResultShown(false);
							console.log("Automatically processing text after login");

							// Process the text directly instead of calling humanizerHandler
							try {
								// Execute humanization and get the result
								const result = await humanizeFx({
									text: pendingText,
									isEnhancedModel: isProModel,
								});
								console.log("Auto-humanize successful");
								setIsHumanizedResultShown(true);

								// For trial users, run AI detection on output text
								if (isTrialUser) {
									// Get the humanized text directly from the store to ensure we have the latest value
									const currentHumanizedText = $humanizedText.getState();
									console.log(
										"TRIAL USER: Running output AI detection after auto-humanize",
									);
									console.log("Output text available:", !!currentHumanizedText);

									// Use a shorter timeout to run AI detection sooner after text is available
									setTimeout(() => {
										// Get the latest humanized text from the store
										const latestHumanizedText = $humanizedText.getState();
										console.log(
											"Running output AI detection with text:",
											!!latestHumanizedText,
										);

										if (latestHumanizedText) {
											// Decrement requests for output AI detection
											if (user?.subscription && user.subscription.requestsLeft > 0) {
												user.subscription.requestsLeft--;
												console.log(
													"Decremented request for output AI detection, remaining:",
													user.subscription.requestsLeft,
												);
											}
											setAiReDetectShow(true);
											aiReDecectFx({ text: latestHumanizedText });
										} else {
											console.error("No humanized text available for AI detection");
										}
									}, 800);
								}

								// Only remove from localStorage after successful processing
								localStorage.removeItem("pendingHumanizeText");
							} catch (error) {
								console.error("Auto-humanize Error:", error);
								// Restore the request count
								if (user?.subscription) {
									user.subscription.requestsLeft++;
								}
							}
						} else {
							console.log("Not enough requests left for auto-humanize");
							makeToast("error", t("humanizer.toast.noRequests"));
						}
					} else {
						console.log("Text validation failed for auto-humanize");
						localStorage.removeItem("pendingHumanizeText");
					}
				};

				// Process the text after a longer delay to ensure the state is updated
				setTimeout(processStoredText, 1000);
			}
		} else {
			setMaxWords(300); // Default if no user/sub
		}
	}, [user]);

	// --- SMOOTHER RESIZE HANDLE LOGIC ---
	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			dragInfoRef.current = { startY: e.clientY, startHeight: textareaHeight };
			setIsDragging(true);
			setManuallyResized(true);
		},
		[textareaHeight],
	);

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!isDragging || !dragInfoRef.current) return;
			const deltaY = e.clientY - dragInfoRef.current.startY;
			const newHeight = dragInfoRef.current.startHeight + deltaY;
			const clampedHeight = Math.min(Math.max(newHeight, minHeight), maxHeight);
			setTextareaHeight(clampedHeight);
			if (inputTextareaRef.current) inputTextareaRef.current.style.height = `${clampedHeight}px`;
			if (outputTextareaRef.current)
				outputTextareaRef.current.style.height = `${clampedHeight}px`;
		},
		[isDragging, minHeight, maxHeight],
	);

	const handleMouseUp = useCallback(() => {
		if (isDragging) {
			setIsDragging(false);
			dragInfoRef.current = null;
		}
	}, [isDragging]);

	useEffect(() => {
		// Global listeners for drag
		if (isDragging) {
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
			document.body.style.cursor = "ns-resize";
			document.body.style.userSelect = "none";
		} else {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		}
		return () => {
			// Cleanup
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		};
	}, [isDragging, handleMouseMove, handleMouseUp]);

	// --- Event Handlers (Using Snippet 1 Logic) ---
	const textAreaChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		humanizerInputHandler(value);
		if (inputTextareaRef.current && !manuallyResized) {
			inputTextareaRef.current.style.height = "auto";
			const scrollHeight = inputTextareaRef.current.scrollHeight;
			const newHeight = Math.min(Math.max(scrollHeight, minHeight), defHeight);
			inputTextareaRef.current.style.height = `${newHeight}px`;
			setTextareaHeight(newHeight);
			if (outputTextareaRef.current) outputTextareaRef.current.style.height = `${newHeight}px`;
		} else if (outputTextareaRef.current && inputTextareaRef.current) {
			outputTextareaRef.current.style.height = inputTextareaRef.current.style.height;
		}
		if (manuallyResized) {
			setManuallyResized(false);
		} // Reset manual flag on change
	};

	const pasteActionClickHandler = async () => {
		// Snippet 1 Logic (with refinements)
		try {
			const text = await navigator.clipboard.readText();
			humanizerInputHandler(text);
			if (inputTextareaRef.current) {
				inputTextareaRef.current.focus();
				setManuallyResized(false); // Allow auto-resize
				requestAnimationFrame(() => {
					// Ensure DOM update before height check
					if (inputTextareaRef.current) {
						textAreaChangeHandler({
							target: inputTextareaRef.current,
						} as React.ChangeEvent<HTMLTextAreaElement>);
					}
				});
			}
		} catch (err) {
			console.error("Paste Error:", err);
			makeToast("error", t("humanizer.toast.pasteError"));
		}
	};

	const tryASampleClickHandler = () => {
		// Snippet 1 Logic (with refinements)
		humanizerInputHandler(SAMPLE_TEXT);
		if (inputTextareaRef.current) {
			inputTextareaRef.current.focus();
			setManuallyResized(false); // Allow auto-resize
			requestAnimationFrame(() => {
				// Ensure DOM update before height check
				if (inputTextareaRef.current) {
					textAreaChangeHandler({
						target: inputTextareaRef.current,
					} as React.ChangeEvent<HTMLTextAreaElement>);
				}
			});
		}
	};

	const humanizerHandler = async () => {
		// This now covers all cases:
		// - Not yet logged in (user object is empty, `!user` is true)
		// - Logged in but subscription ended/invalid (`!user.subscription` is true)
		// - Anonymous TRIAL user who ran out of requests (`enoughRequests` is false)
		const requiredRequests = isTrial ? 2 : 1; // Trial users need 2 requests for humanize + auto-scan
		const enoughRequests = user?.subscription?.requestsLeft >= requiredRequests;
	
		if (!user || !user.subscription || !enoughRequests) {
			// For any of the above, show the animation gate to prompt registration/upgrade.
			if (animationStatus === "hidden") {
				setAnimationStatus("animating");
			}
			// Save text for post-login processing ONLY if the user is not fully registered.
			if (!user || user.role === 'ANONYMOUS') {
				localStorage.setItem("pendingHumanizeText", inputValue);
			}
			return;
		}

		// --- At this point, user is either PAID or TRIAL with enough requests ---

		// Validate input
		const check = inputValue.trim().length > 2;
		const currentWordCount = countWords(inputValue);
		const isTooManyWords = currentWordCount > maxWords;

		if (!check) {
			makeToast("error", t("history.toast.tooShort"));
			return;
		}

		if (isTooManyWords) {
			makeToast("error", t("humanizer.toast.tooLong", { count: maxWords }));
			return;
		}

		// Process the request
		user.subscription.requestsLeft -= 1; // Deduct the first request for humanization
		setIsHumanizedResultShown(false);

		try {
			await humanizeFx({ text: inputValue, isEnhancedModel: isProModel });
			setIsHumanizedResultShown(true);

			// For trial users, automatically run AI detection on the output and deduct second request
			if (isTrial) {
				user.subscription.requestsLeft -= 1; // Deduct the second request for AI scan
				console.log("Trial user detected, running output AI detection");
				setTimeout(() => {
					const currentHumanizedText = $humanizedText.getState();
					if (currentHumanizedText) {
						setAiReDetectShow(true);
						aiReDecectFx({ text: currentHumanizedText });
					}
				}, 1000);
			}
		} catch (error) {
			console.error("Humanize Error:", error);
			makeToast("error", t("humanizer.toast.errorGeneral"));
			user.subscription.requestsLeft += requiredRequests; // Restore requests on failure
		}
	};

	// Special function to handle trial user flow with guaranteed AI detection
	const handleTrialUserFlow = async () => {
		// Check if user has enough requests first
		if (!user?.subscription || user.subscription.requestsLeft <= 0) {
			makeSubscriptionToast(true, false, t, handleUpgrade);
			return;
		}

		// Validate input first
		const check = inputValue.trim().length > 2;
		const currentWordCount = countWords(inputValue);
		const isTooManyWords = currentWordCount > maxWords;

		if (!check) {
			makeToast("error", t("history.toast.tooShort"));
			return;
		}

		if (isTooManyWords) {
			makeToast("error", t("humanizer.toast.tooLong", { count: maxWords }));
			return;
		}

		// Step 1: Run AI detection on input
		console.log("Trial user flow: Step 1 - Running input AI detection");
		setTextDetectionShow(true);
		await aiDetectFx({ text: inputValue });

		// Step 2: Run humanization
		console.log("Trial user flow: Step 2 - Running humanization");
		user.subscription.requestsLeft--;
		setIsHumanizedResultShown(false);

		try {
			await humanizeFx({ text: inputValue, isEnhancedModel: isProModel });
			setIsHumanizedResultShown(true);

			// Step 3: Run AI detection on output
			console.log("Trial user flow: Step 3 - Running output AI detection");
			setTimeout(() => {
				setAiReDetectShow(true);
				aiReDecectFx({ text: humanizedText || "" });
			}, 1000);
		} catch (error) {
			console.error("Trial User Flow Error:", error);
			makeToast("error", t("humanizer.toast.errorGeneral"));
			user.subscription.requestsLeft++;
		}
	};

	const rehumanizerHandler = async () => {
		// Validate input first
		const check = inputValue.trim().length > 2;
		const currentWordCount = countWords(inputValue);
		const isTooManyWords = currentWordCount > maxWords;

		if (!check) {
			makeToast("error", t("history.toast.tooShort"));
			return;
		}

		if (isTooManyWords) {
			makeToast("error", t("humanizer.toast.tooLong", { count: maxWords }));
			return;
		}

		// For rehumanize, we don't check subscription requests
		// It's allowed even with 0 requests left (up to 3 times)
		setIsHumanizedResultShown(false); // Reset UI state before attempt
		setAiReDetectShow(false); // Also reset AI detection display

		try {
			console.log("Attempting rehumanization for input:", inputValue);
			const newHumanizedText = await rehumanizeFx({
				text: inputValue,
				isEnhancedModel: isProModel,
			});

			// This block executes only if rehumanizeFx is successful and returns text
			if (newHumanizedText) {
				setIsHumanizedResultShown(true);
				console.log("Rehumanization successful. New text received.");
				// $humanizedText store is updated via Effector's sample logic, but newHumanizedText is the direct result.

				console.log("Running free AI detection on rehumanized output:", newHumanizedText);
				setAiReDetectShow(true); // Show AI detection component
				await aiReDecectFx({ text: newHumanizedText, isPostRehumanizationCheck: true });
				console.log("AI re-detection attempt finished for rehumanized text.");
			} else {
				// Handle cases where rehumanization might succeed but not return text (should be rare)
				console.error("Rehumanization seemed successful but no new text was returned.");
				makeToast("error", t("humanizer.toast.errorGeneral"));
				setIsHumanizedResultShown(false);
			}
		} catch (error: any) {
			console.error("Re-Humanize Error caught in handler:", error);
			setIsHumanizedResultShown(false); // Ensure humanized result is not shown on error
			setAiReDetectShow(false); // Hide AI detection results as well

			if (error?.response?.data?.message?.includes("maximum limit of 3 rehumanizations")) {
				// Show subscription toast instead of error toast when hitting the rehumanization limit
				makeSubscriptionToast(!!isTrial, false, t, handleUpgrade);
			} else if (error?.response?.data?.message) {
				// Display specific backend error message if available
				makeToast("error", error.response.data.message);
			} else {
				makeToast("error", t("humanizer.toast.errorGeneral"));
			}
		}
	};

	// --- DETECT AI - Independent Results Logic (from previous step) ---
	const detectAi = async (type: string = "") => {
		const textToCheck = type === "redetect" ? humanizedText : inputValue;
		if (!textToCheck || textToCheck.trim().length <= 2) {
			makeToast("error", t("humanizer.toast.fillRequired"));
			return;
		}
		const detectionMaxWords = user?.subscription?.maxWords || 300;
		const isTooManyWords = countWords(textToCheck) > detectionMaxWords;
		if (isTooManyWords) {
			makeToast("error", t("humanizer.toast.tooLong", { count: detectionMaxWords }));
			return;
		}

		let requestDecremented = false;
		if (user?.subscription) {
			if (user.subscription.requestsLeft > 0) {
				user.subscription.requestsLeft--;
				requestDecremented = true;
			} else {
				makeSubscriptionToast(!!isTrial, false, t, handleUpgrade);
				return;
			}
		}

		try {
			// Trigger Scan(s)
			if (type === "redetect") {
				setAiReDetectShow(true); // Set only this state
				await aiReDecectFx({ text: humanizedText || "" });
			} else {
				// Default is input scan
				setTextDetectionShow(true); // Set only this state
				await aiDetectFx({ text: inputValue });
			}
		} catch (error) {
			console.error(`AI Detect Error (${type || "input"}):`, error);
			makeToast("error", t("humanizer.toast.errorDetect"));
			if (requestDecremented && user?.subscription) {
				user.subscription.requestsLeft++;
			} // Revert count
			if (type === "redetect") setAiReDetectShow(false);
			else setTextDetectionShow(false); // Hide only failed area
		}
	};

	const handleEnhancedModelClick = () => {
		console.log('handleEnhancedModelClick called');
		console.log('isTrial:', isTrial);
		console.log('user:', user);
		console.log('user?.subscription:', user?.subscription);
		
		// Logic from snippet 1 (corrected)
		if (isTrial || !user?.subscription) {
			console.log('Showing stealth model popup');
			// Use the new StealthModelPopup instead of toast
			if ((window as any).showStealthModelPopupFromHumanizer) {
				(window as any).showStealthModelPopupFromHumanizer();
			} else {
				// Fallback to old toast if function not available
				makeEnhancedModelToast(t, true, handleUpgrade);
			}
		} else {
			console.log('User has subscription, toggling Pro model');
			const nextState = !isProModel;
			setIsProModel(nextState);
			makeToast(
				"success",
				nextState ? t("humanizer.toast.stealthOn") : t("humanizer.toast.stealthOff"),
			);
		}
	};

	// --- Render Helpers ---
	const renderActions = () => {
		// Logic from snippet 1
		return !inputValue ? (
			<div className={styles.ActionWrapper}>
				<div className={styles.ActionWrapper__container}>
					<Action
						clickHandler={tryASampleClickHandler}
						icon={<SampleIcon />}
						text={t(`humanizer.trySample`)}
					/>
					<Action
						clickHandler={pasteActionClickHandler}
						icon={<CopyIcon width={18} fill="white" height={18} />}
						text={t(`humanizer.pasteText`)}
					/>
				</div>
			</div>
		) : null;
	};

	const disableHumanizeButton = () => {
		// Logic from snippet 1
		const wordCount = countWords(inputValue);
		if (inputValue.trim().length <= 2) return true;
		if (user?.subscription) {
			if (wordCount > maxWords) return true;
		} else {
			if (wordCount > 300) return true;
		}
		if (humanizerLoading) return true;
		return false;
	};

	// --- Component Return JSX (Applying Layout/Loader/Tooltip Changes) ---
	// Debug logging before render
	console.log("[HumanizerInput RENDER] user:", user);
	console.log("[HumanizerInput RENDER] subscription:", user?.subscription);
	console.log("[HumanizerInput RENDER] title:", user?.subscription?.title);
	console.log("[HumanizerInput RENDER] Will display:", user?.subscription?.title || "General");
	
	return (
		<div
			ref={ref}
			className={`${styles.HumanizerInput} ${props.className || ""} ${
				textDetectionShow || aiReDetectShow ? styles.HumanizerInput_animated : ""
			}`}
		>
			<div className={styles.HumanizerInput__flexWrapper}>
				{/* === Input Column === */}
				<div className={`${styles.HumanizerInput__input}`}>
					<div className={`${styles.HumanizerInput__topBar}`}>
						<p className={styles.HumanizerInput__title}>{t(`humanizer.yourInput`)}</p>
						<p className={styles.HumanizerInput__wordCountInfo}>
							<span className={styles.HumanizerInput__basicPlan}>
								{user?.subscription?.title || "General"} Plan
							</span>{" "}
							<span
								className={`${
									countWords(inputValue) > maxWords ? styles.HumanizerInput__redColor : ""
								}`}
							>
								{countWords(inputValue)} / {maxWords} {t("history.words")}
							</span>
						</p>
					</div>
					<div className={styles.HumanizerInput__fieldWrapper}>
						{renderActions()}
						<textarea
							ref={inputTextareaRef}
							onChange={textAreaChangeHandler}
							value={inputValue}
							className={`${styles.HumanizerInput__field} ${styles.resizableTextarea}`}
							placeholder={t(`humanizer.placeholder1`)}
							style={{
								height: `${textareaHeight}px`,
								minHeight: `${minHeight}px`,
								maxHeight: `${maxHeight}px`,
							}}
						/>
					</div>
					<div className={`${styles.buttons}`}>
						<div className={styles.leftContent}>
							{/* === STEALTH MODEL SWITCH with INFO ICON (Corrected Structure) === */}
							<TooltipProvider delayDuration={300}>
								<Tooltip>
									{/* TooltipTrigger wraps the entire interactive group */}
									<TooltipTrigger asChild className={styles.tooltipTriggerWrapper}>
										{/* This div receives the proModelSelector styles */}
										<div className={styles.HumanizerInput__proModelSelector}>
											<div className={styles.labelSwitchContainer}>
												<span>Stealth Model</span>
												<InfoIcon className={styles.infoIcon} />
												<Switch
													checked={isProModel}
													onCheckedChange={handleEnhancedModelClick}
												/>
											</div>
										</div>
									</TooltipTrigger>
									{/* TooltipContent remains the same */}
									<TooltipContent
										className="humanizerTooltip"
										side="top"
										align="center"
										sideOffset={5}
									>
										<p>
											The Stealth model is our best model designed to bypass the hardest
											detectors like latest <b>GPTZero update and Turnitin</b>
											<br />
											<br />
											Be aware that it may cause significant text alterations, as this
											model relies heavily on lexical paraphrasing. Use free
											re-humanizaton in case of errors.
										</p>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
							{/* === END OF STEALTH SWITCH === */}
						</div>
						<div className={styles.rightContent}>
							{/* Scan Button (Triggers INPUT scan only) */}
							<ButtonPrimary
								onClick={() => detectAi("")}
								className={styles.buttons__centeringMod}
								icon={<AiIcon />}
								colorScheme="dark-orange"
								disabled={inputValue.trim().length === 0 || aiDetectLoading}
							>
								{aiDetectLoading ? t("humanizer.scanningBtn") : t(`humanizer.scanForAi`)}
							</ButtonPrimary>
							{/* Humanize Button */}
							<ButtonPrimary
								onClick={() => {
									// Check if user has any requests left first
									if (user?.subscription && user.subscription.requestsLeft <= 0) {
										makeSubscriptionToast(!!isTrial, false, t, handleUpgrade);
										return;
									}

									// Check if user is trial
									const isTrialUser = user?.subscription?.title?.toUpperCase() === "TRIAL";
									console.log("Button clicked, is trial user:", isTrialUser);

									if (isTrialUser) {
										// For trial users, handle special flow
										handleTrialUserFlow();
									} else {
										// For regular users, use normal flow
										humanizerHandler();
									}
								}}
								className={styles.buttons__centeringMod}
								icon={<StickIcon />}
								colorScheme="orange"
								disabled={disableHumanizeButton()}
							>
								{humanizerLoading
									? t("humanizer.processingBtn")
									: t("humanizer.humanizeBtn")}
							</ButtonPrimary>
						</div>
					</div>
					<div className={styles.textDetectionContainer}>
						{textDetectionShow && (
							<TextDetectionResult
								data={aiDetectRes}
								bottomAiResultBadge={false}
								loading={aiDetectLoading}
								isInput={true} // This is the input side
								isTrial={isTrial} // Show fake results for trial users
							/>
						)}
					</div>
				</div>{" "}
				{/* End Input Column */}
				{/* === Result Column === */}
				<div className={`${styles.HumanizerInput__result}`}>
					<div className={`${styles.HumanizerInput__copyContainer}`}>
						<p className={styles.HumanizerInput__title}> {t(`humanizer.result`)} </p>
						{/* Added base class for styling */}
						<CopyElement
							className={`${styles.copyElementBaseClass} ${
								!humanizedText || humanizedText.length === 0 ? styles.copyDisabled : ""
							}`}
							onClick={() => {
								if (humanizedText?.length) {
									navigator.clipboard.writeText(humanizedText);
									makeToast("success", t("humanizer.toast.copied"));
								}
							}}
						/>
					</div>
					<div className={styles.resultContentArea}>
						{animationStatus !== "hidden" ? (
							<div className={styles.persistentAnimationContainer}>
								<NonLoggedInAnimation
									inputText={inputValue}
									onUnlock={() => {
										setAnimationStatus("hidden");

										// Save the current text to localStorage before navigating
										if (inputValue && inputValue.trim().length > 2) {
											console.log(
												"Saving text before login:",
												inputValue.substring(0, 20) + "...",
											);
											localStorage.setItem("pendingHumanizeText", inputValue);
										}

										navigate("/auth");
									}}
									onAnimationComplete={() => {
										// Don't change state here - let the animation persist until user interaction
										console.log("Animation completed but keeping status as 'animating'");
									}}
								/>
							</div>
						) : humanizerLoading ? ( // If loading, show Loader with bar
							<div className={styles.Loader}>
								<p> {t(`humanizer.processing`)}</p>
								{/* Restored Loader Bar Structure */}
								<div className={styles.Loader__bar}>
									<div className={styles.Loader__barAnimated}></div>
								</div>
							</div>
						) : (
							// Otherwise, show Textarea
							<textarea
								ref={outputTextareaRef}
								readOnly
								value={humanizedText || ""}
								placeholder={t(`humanizer.resultPlaceholder`)}
								className={`${styles.HumanizerInput__result_text} ${
									styles.resizableTextarea
								} ${!humanizerLoading && humanizedText ? styles.animatedFadeIn : ""}`}
								style={{
									height: `${textareaHeight}px`,
									minHeight: `${minHeight}px`,
									maxHeight: `${maxHeight}px`,
								}}
							/>
						)}
					</div>

					{/* Bottom Bar (Result Column) */}
					<div
						className={styles.buttons_2}
						style={humanizerLoading ? { marginTop: "auto" } : {}}
					>
						{/* Use snippet 1 condition: Show only if humanizedText exists */}
						{humanizedText && ( // Show buttons only if there is result text
							<>
								<ButtonPrimary
									onClick={() => detectAi("redetect")} // Scan Output
									className={styles.buttons__centeringMod}
									icon={<AiIcon />}
									disabled={reDetectLoading || humanizerLoading} // Snippet 1 logic
									colorScheme="dark-orange"
								>
									{reDetectLoading ? t("humanizer.scanningBtn") : t(`humanizer.rescanAi`)}
								</ButtonPrimary>
								<ButtonPrimary
									onClick={() => {
										rehumanizerHandler();
									}} // Snippet 1 logic (no state reset here)
									className={styles.buttons__centeringMod}
									icon={<StickIcon />}
									colorScheme="orange"
									disabled={reDetectLoading || humanizerLoading} // Snippet 1 logic
								>
									{humanizerLoading
										? t("humanizer.processingBtn")
										: t("humanizer.rehumanize")}
								</ButtonPrimary>
							</>
						)}
					</div>
					<div className={styles.textDetectionContainer}>
						{aiReDetectShow && (
							<TextDetectionResult
								data={reDetectAi}
								bottomAiResultBadge={false}
								loading={reDetectLoading}
								isInput={false} // This is the output side
								isTrial={isTrial} // Show fake results for trial users
							/>
						)}
					</div>
				</div>
				{/* End Result Column */}
			</div>
			{/* End Flex Wrapper */}
			{/* RESIZE SLIDER (Using useCallback version of handler) */}
			<div ref={resizeHandleRef} className={styles.resizeHandle} onMouseDown={handleMouseDown} />
		</div>
	);
};
